/**
 * Hook del modulo M4 — Cruscotto Direzionale.
 *
 * Solo letture: M4 non scrive niente, quindi non c'è nessuna `useMutation`.
 * La giornata e la settimana prendono un parametro `data`, che è anche la chiave
 * di cache: cambiare giorno non rifà le altre chiamate.
 */

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type {
  M4ConfezionamentoResponse,
  M4GiornataResponse,
  M4MateriePrimeResponse,
  M4ProduzioneResponse,
  M4SettimanaResponse,
  M4StoricoResponse,
  M4UsciteResponse,
} from "@/types/api";

/** `undefined` = oggi, deciso dal backend. */
function conData(base: string, data?: string): string {
  return data ? `${base}?data=${data}` : base;
}

export function useM4Giornata(data?: string) {
  return useQuery({
    queryKey: ["m4", "giornata", data ?? "oggi"],
    queryFn: () => apiFetch<M4GiornataResponse>(conData("/api/v1/m4/giornata", data)),
    staleTime: 60_000,
  });
}

export function useM4Settimana(data?: string) {
  return useQuery({
    queryKey: ["m4", "settimana", data ?? "oggi"],
    queryFn: () => apiFetch<M4SettimanaResponse>(conData("/api/v1/m4/settimana", data)),
    staleTime: 60_000,
  });
}

export function useM4Uscite() {
  return useQuery({
    queryKey: ["m4", "uscite"],
    queryFn: () => apiFetch<M4UsciteResponse>("/api/v1/m4/uscite"),
    staleTime: 60_000,
  });
}

export function useM4Confezionamento() {
  return useQuery({
    queryKey: ["m4", "confezionamento"],
    queryFn: () => apiFetch<M4ConfezionamentoResponse>("/api/v1/m4/confezionamento"),
    staleTime: 60_000,
  });
}

export function useM4Produzione() {
  return useQuery({
    queryKey: ["m4", "produzione"],
    queryFn: () => apiFetch<M4ProduzioneResponse>("/api/v1/m4/produzione"),
    staleTime: 60_000,
  });
}

export function useM4MateriePrime() {
  return useQuery({
    queryKey: ["m4", "materie-prime"],
    queryFn: () => apiFetch<M4MateriePrimeResponse>("/api/v1/m4/materie-prime"),
    staleTime: 60_000,
  });
}

/**
 * La serie storica del portafoglio: il primo caricamento costa qualche secondo
 * (il motore di evadibilità gira su diciotto giornate), poi il backend la tiene
 * in memoria. `staleTime` alto perché cambia solo con uno snapshot nuovo.
 */
export function useM4Storico() {
  return useQuery({
    queryKey: ["m4", "storico"],
    queryFn: () => apiFetch<M4StoricoResponse>("/api/v1/m4/storico"),
    staleTime: 10 * 60_000,
  });
}
